import { config } from "@/config";

import { IdentityError, type IdentityAdapter } from "./identity";

/** Without the Firebase web config the portal still loads and explains why sign-in can't work. */
class NotConfiguredIdentity implements IdentityAdapter {
  private fail(): never {
    throw new IdentityError(
      "not-configured",
      "Sign-in isn't set up for this build (Firebase web config missing). See the README.",
    );
  }
  signInWithGoogle = async () => this.fail();
  signInWithEmail = async () => this.fail();
  createAccount = async () => this.fail();
  sendPasswordReset = async () => this.fail();
  clear = async () => undefined;
}

/**
 * Loads the Firebase SDK only when someone signs in (it is most of the code size, and a signed-in
 * person never needs it: the API cookie does the rest).
 */
class LazyFirebaseIdentity implements IdentityAdapter {
  private loading: Promise<IdentityAdapter> | null = null;

  private adapter(): Promise<IdentityAdapter> {
    this.loading ??= import("./firebase/firebase-identity")
      .then(({ FirebaseIdentity }) => new FirebaseIdentity(config.firebase))
      .catch((error: unknown) => {
        this.loading = null; // a failed download can be tried again
        throw new IdentityError("network", "Couldn't load sign-in. Check your connection and try again.", {
          cause: error,
        });
      });
    return this.loading;
  }

  signInWithGoogle = async () => (await this.adapter()).signInWithGoogle();
  signInWithEmail = async (email: string, password: string) =>
    (await this.adapter()).signInWithEmail(email, password);
  createAccount = async (email: string, password: string) =>
    (await this.adapter()).createAccount(email, password);
  sendPasswordReset = async (email: string) => (await this.adapter()).sendPasswordReset(email);
  clear = async () => {
    if (this.loading) await (await this.loading).clear();
  };
}

export function createIdentity(): IdentityAdapter {
  return config.firebaseConfigured ? new LazyFirebaseIdentity() : new NotConfiguredIdentity();
}
